import { useState, type JSX } from 'react';
import { LayoutGrid, Search } from 'lucide-react';

import type { TrialBarDto } from '@ctms/contracts';

import { useTrialBars } from './use-trial-bars-query.js';

interface TrialBarsProps {
  /** RBAC gate (visits:read) — the row is hidden without it, like the picker. */
  canRead: boolean;
  /** Trial the whole cockpit works on; its button is the highlighted one. */
  activeTrialId: number | null;
  /** A bar button click sets the active trial (REQ-VIS-005). */
  onSelectTrial: (trialId: number) => void;
  /** Grid icon → the bar definition window (A5). */
  onOpenGrid: () => void;
  /** Loupe icon → the trial search window (A6). */
  onOpenSearch: () => void;
}

/**
 * Wariant A row in the cockpit header (#7, REQ-VIS-005, frozen feature
 * trial-bars-and-search.feature): [bar title + pager] [dropdown] [trial
 * buttons] [grid] [loupe].
 *
 * - Title click cycles bars 1 → 2 → 3 → 1 (frozen scenario); the dropdown
 *   picks a bar directly — the bar count is UNLIMITED (Piotr odp.3) and with
 *   many bars the menu beats blind clicking.
 * - A trial button click sets the active trial; the button of the ACTIVE
 *   trial is the highlighted one (legacy paski_grup_badan.png).
 * - Icon buttons carry text labels (Piotr odp.2: "może być z podpisami
 *   tekstowymi" — the default realization).
 * - Items are already filtered server-side to ACTIVE trials (REQ-TRIAL-002),
 *   so the row renders exactly what the DTO carries.
 * - Overflow: the button strip scrolls horizontally — the EMERGENCY fallback
 *   only (live-preview in the definition window is the real guardian, D1).
 */
export function TrialBars({
  canRead,
  activeTrialId,
  onSelectTrial,
  onOpenGrid,
  onOpenSearch,
}: TrialBarsProps): JSX.Element | null {
  const barsQuery = useTrialBars(canRead);
  const [currentBarIndex, setCurrentBarIndex] = useState(0);

  if (!canRead) {
    return null;
  }

  const bars: TrialBarDto[] = barsQuery.data?.bars ?? [];
  // The remembered index survives bar list changes; clamp into range.
  const safeIndex = bars.length === 0 ? 0 : Math.min(currentBarIndex, bars.length - 1);
  const currentBar = bars[safeIndex];

  const cycleToNextBar = (): void => {
    if (bars.length > 1) {
      setCurrentBarIndex((safeIndex + 1) % bars.length);
    }
  };

  return (
    <div
      data-testid="trial-bars-row"
      className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-1"
    >
      {currentBar ? (
        <>
          <button
            type="button"
            data-testid="trial-bar-title"
            onClick={cycleToNextBar}
            title="Kliknij, aby przełączyć na kolejny pasek"
            className="max-w-[180px] truncate rounded px-1.5 py-0.5 text-xs font-bold tracking-wide text-cyan-300 hover:bg-slate-800"
          >
            {currentBar.name}
          </button>
          <span data-testid="trial-bar-pager" className="text-[10px] text-slate-400">
            {safeIndex + 1}/{bars.length}
          </span>
          {bars.length > 1 ? (
            <select
              data-testid="trial-bar-select"
              aria-label="Wybierz pasek badań"
              value={String(currentBar.id)}
              onChange={(event) => {
                const chosen = bars.findIndex((bar) => String(bar.id) === event.target.value);
                if (chosen >= 0) {
                  setCurrentBarIndex(chosen);
                }
              }}
              className="max-w-[140px] rounded-md border border-slate-700 bg-slate-950 px-1.5 py-1 text-xs text-slate-100 scheme-dark"
            >
              {bars.map((bar) => (
                <option key={bar.id} value={String(bar.id)}>
                  {bar.name}
                </option>
              ))}
            </select>
          ) : null}
          <div
            data-testid="trial-bar-items"
            className="flex min-w-0 items-center gap-1 overflow-x-auto"
          >
            {currentBar.items.map((item) => {
              const isActive = item.trialId === activeTrialId;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`trial-button-${item.trialId}`}
                  onClick={() => onSelectTrial(item.trialId)}
                  title={`${item.shortTitle} (ośrodek ${item.siteNumber})`}
                  aria-pressed={isActive}
                  className={
                    isActive
                      ? 'rounded-md bg-cyan-600 px-2 py-1 text-xs font-bold text-white'
                      : 'rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs font-medium text-slate-200 hover:border-cyan-700 hover:text-cyan-200'
                  }
                >
                  {item.shortcut}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <span className="px-1 text-[11px] text-slate-500">
          {barsQuery.isError
            ? 'Paski niedostępne'
            : 'Brak pasków — zdefiniuj je pod siatką'}
        </span>
      )}
      <button
        type="button"
        data-testid="trial-bars-grid"
        onClick={onOpenGrid}
        title="Definiuj ulubione paski badań"
        className="flex items-center gap-1 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-cyan-700 hover:text-cyan-200"
      >
        <LayoutGrid className="h-3.5 w-3.5" />
        <span>Paski</span>
      </button>
      <button
        type="button"
        data-testid="trial-bars-search"
        onClick={onOpenSearch}
        title="Szukaj badania (skrót, nazwa, numer ośrodka)"
        className="flex items-center gap-1 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-cyan-700 hover:text-cyan-200"
      >
        <Search className="h-3.5 w-3.5" />
        <span>Szukaj</span>
      </button>
    </div>
  );
}
